import React, { useEffect } from "react";
import { Printer, X } from "lucide-react";
import { PaymentOrder } from "../../../types/payment";
import { getPackageLabel, PAYMENT_SOURCE_LABELS, ORDER_STATUS_LABELS, formatMoney, formatDateTime } from "../paymentFormat";

interface InvoiceDetailModalProps {
  order: PaymentOrder | null;
  onClose: () => void;
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{value}</span>
    </div>
  );
}

export default function InvoiceDetailModal({ order, onClose }: InvoiceDetailModalProps) {
  // Yazdırmada sadece fatura kartı görünsün (index.css @media print kuralı).
  useEffect(() => {
    if (!order) return;
    document.body.classList.add("printing-invoice");
    return () => document.body.classList.remove("printing-invoice");
  }, [order]);

  if (!order) return null;

  const billing = order.billingInfo || null;
  const buyerName = billing?.type === "corporate" && billing.companyName
    ? billing.companyName
    : `${billing?.firstName || order.user?.firstName || ""} ${billing?.lastName || order.user?.lastName || ""}`.trim();
  const addressLine = [billing?.address, billing?.district, billing?.city].filter(Boolean).join(", ");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 print:static print:bg-transparent print:p-0">
      <div className="invoice-print-root flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl print:max-h-none print:shadow-none">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 print:hidden">
          <h2 className="text-base font-semibold text-slate-900">Ödeme / Fatura Detayı</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => window.print()}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Yazdır"
            >
              <Printer className="h-5 w-5" />
            </button>
            <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Ödeme</h3>
            <Row label="Durum" value={ORDER_STATUS_LABELS[order.status] || order.status} />
            <Row label="Kaynak" value={PAYMENT_SOURCE_LABELS[order.provider] || order.provider} />
            <Row label="Paket" value={getPackageLabel(order)} />
            <Row label="Tutar" value={formatMoney(order.paidAmount)} />
            {Number(order.feeAmount) > 0 && <Row label="Vade Farkı" value={formatMoney(order.feeAmount)} />}
            {order.installment > 1 && <Row label="Taksit" value={`${order.installment}`} />}
            <Row label="Tarih" value={formatDateTime(order.paidAt || order.createdAt)} />
            <Row label="Erişim Bitişi" value={order.assignment?.endsAt ? formatDateTime(order.assignment.endsAt) : undefined} />
            <Row label="Sağlayıcı Referansı" value={order.providerReference || undefined} />
            <Row label="İşlem No" value={order.id} />
            {order.createdBy && (
              <Row label="Tanımlayan" value={`${order.createdBy.firstName || ""} ${order.createdBy.lastName || ""}`.trim()} />
            )}
            <Row label="Açıklama" value={order.note || undefined} />
          </div>

          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Fatura Bilgileri</h3>
            {billing && (buyerName || addressLine) ? (
              <>
                <Row label="Fatura Tipi" value={billing.type === "corporate" ? "Kurumsal" : "Bireysel"} />
                <Row label={billing.type === "corporate" ? "Firma Unvanı" : "Ad Soyad"} value={buyerName} />
                {billing.type === "corporate" ? (
                  <>
                    <Row label="Vergi Dairesi" value={billing.taxOffice} />
                    <Row label="Vergi No" value={billing.taxNumber} />
                  </>
                ) : (
                  <Row label="TC Kimlik No" value={billing.identityNumber} />
                )}
                <Row label="Adres" value={addressLine} />
                <Row label="Telefon" value={billing.phone} />
                <Row label="E-posta" value={billing.email} />
              </>
            ) : (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
                Bu ödeme için fatura bilgisi girilmemiş.
              </p>
            )}
            {order.user && (
              <p className="mt-3 text-xs text-slate-400">
                Hesap: {`${order.user.firstName || ""} ${order.user.lastName || ""}`.trim()} · {order.user.email}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
